import { useState } from 'react';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import { useLoyalty } from '../../../apps/hooks/loyalty/useLoyalty';
import {
  CustomerSearch,
  CustomerBalanceCard,
  TransactionHistory,
  EarnPointsModal,
  RedeemPointsModal,
  UpdateTierModal,
} from './components';

export default function LoyaltyPage() {
  const {
    customerBalance,
    transactionHistory,
    tierDefinitions,
    isLoadingBalance,
    isLoadingTransactions,
    isLoadingTiers,
    error,
    selectCustomer,
    earnPoints,
    redeemPoints,
    updateTier,
    loadMoreTransactions,
    clearError,
  } = useLoyalty();

  const [showEarnModal, setShowEarnModal] = useState(false);
  const [showRedeemModal, setShowRedeemModal] = useState(false);
  const [showUpdateTierModal, setShowUpdateTierModal] = useState(false);

  const handleCustomerSearch = (customerId: string) => {
    selectCustomer(customerId);
  };

  const handleEarnPoints = async (
    points: number,
    activityType: 'Purchase' | 'Referral' | 'RewardRedemption',
    orderId?: string,
  ) => {
    if (!customerBalance) {
      return;
    }
    await earnPoints({
      customerId: customerBalance.customerId,
      points,
      activityType,
      ...(orderId && { orderId }),
    });
    setShowEarnModal(false);
  };

  const handleRedeemPoints = async (points: number) => {
    if (!customerBalance) {
      return;
    }
    await redeemPoints({ customerId: customerBalance.customerId, points });
    setShowRedeemModal(false);
  };

  const handleUpdateTier = async (tier: string) => {
    if (!customerBalance) {
      return;
    }
    await updateTier({ customerId: customerBalance.customerId, newTier: tier });
    setShowUpdateTierModal(false);
  };

  return (
    <Container fluid className="p-4">
      <Row>
        <Col>
          <h1 className="mb-4">Loyalty Program Management</h1>

          {error && (
            <Alert variant="danger" dismissible onClose={clearError}>
              {error}
            </Alert>
          )}

          <CustomerSearch onSearch={handleCustomerSearch} />

          {customerBalance && (
            <>
              <CustomerBalanceCard
                balance={customerBalance}
                isLoading={isLoadingBalance}
                onEarnPoints={() => setShowEarnModal(true)}
                onRedeemPoints={() => setShowRedeemModal(true)}
                onUpdateTier={() => setShowUpdateTierModal(true)}
              />

              <TransactionHistory
                transactions={transactionHistory?.transactions || []}
                isLoading={isLoadingTransactions}
                hasMore={Boolean(transactionHistory?.lastEvaluatedKey)}
                onLoadMore={() => {
                  if (transactionHistory?.lastEvaluatedKey) {
                    loadMoreTransactions(transactionHistory.lastEvaluatedKey);
                  }
                }}
              />
            </>
          )}

          {customerBalance && (
            <>
              <EarnPointsModal show={showEarnModal} onClose={() => setShowEarnModal(false)} onEarn={handleEarnPoints} />

              <RedeemPointsModal
                show={showRedeemModal}
                onClose={() => setShowRedeemModal(false)}
                onRedeem={handleRedeemPoints}
                currentPoints={customerBalance.availablePoints}
              />

              <UpdateTierModal
                show={showUpdateTierModal}
                onClose={() => setShowUpdateTierModal(false)}
                onUpdate={handleUpdateTier}
                currentTier={customerBalance.tier}
                tierDefinitions={tierDefinitions || []}
                isLoadingTiers={isLoadingTiers}
              />
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
}
